import { API, Auth } from "aws-amplify";
import { Box, Button, Text, VStack, useToast } from "@chakra-ui/react";
import { useState, useEffect, useCallback } from "react";
import { useContextTypeSettingsOrganization } from "../dashboard-layout";
import { listBills } from "../../../graphql/queries";
import {
  Bill,
  BillStatus,
  ListBillsQuery,
  ListBillsQueryVariables,
} from "../../../API";
import { MultiSelect, MultiSelectOption } from "../../../components/select";
import { queryList } from "../../../utils";
import { useUserBookkeepings } from "../../../hooks/use-user-bookkeepings";

const BexioSettings = () => {
  const { selectedOrganization, bookkeepings } =
    useContextTypeSettingsOrganization();
  const [isLoading, setIsLoading] = useState(false);
  const [openBills, setOpenBills] = useState<Bill[]>([]);
  const [selectedBills, setSelectedBills] = useState<MultiSelectOption[]>([]);
  const toast = useToast();

  const { userBookkeepings } = useUserBookkeepings({
    bookkeepings,
  });

  const fetchOpenBills = useCallback(async () => {
    if (!userBookkeepings?.length) return;

    try {
      const allBills = await queryList<
        Bill,
        ListBillsQueryVariables,
        ListBillsQuery
      >(listBills, {
        filter: {
          status: { ne: BillStatus.paid },
        },
      });

      const userBookkeepingIds = new Set(userBookkeepings.map((ub) => ub.id));
      const filteredBills = allBills.filter((bill) =>
        userBookkeepingIds.has(bill.userBookkeepingId)
      );

      setOpenBills(filteredBills);
    } catch (error) {
      console.error("Error fetching open bills:", error);
    }
  }, [userBookkeepings]);

  useEffect(() => {
    fetchOpenBills();
  }, [fetchOpenBills]);

  const handleTriggerBillClose = async () => {
    if (!selectedOrganization?.id || selectedBills.length === 0) return;

    setIsLoading(true);

    try {
      await API.post("expenslyREST", "/accounting/bexio/bill-close", {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`,
        },
        body: {
          organizationId: selectedOrganization.id,
          billIds: selectedBills.map((bill) => bill.value),
        },
      });
      setSelectedBills([]);
      await fetchOpenBills();
      toast({
        title: "Spesenabrechnung abgeschlossen",
        status: "success",
      });
    } catch (err) {
      console.error("Error triggering bill close:", err);
      toast({
        title: "Fehler beim Schliessen der Spesenabrechnung",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if at least one bookkeeping uses Bexio
  if (
    bookkeepings?.every(
      (bookkeeping) => bookkeeping.accountingSystem !== "bexio"
    )
  ) {
    return (
      <Text>
        Deine Organisation verwendet kein Bexio als Buchhaltungssystem.
      </Text>
    );
  }

  return (
    <VStack width="full" height="full" alignItems="start" spacing="20px">
      <VStack alignItems="start" spacing="4px">
        <Text fontSize={18} fontWeight="semibold" color="gray.900">
          bexio
        </Text>
        <Text fontSize="sm" color="gray.600">
          Hier kannst du die Spesenabrechnungen manuell schliessen
        </Text>
      </VStack>
      <Box width="full" maxWidth="600px">
        <MultiSelect
          options={openBills
            .slice()
            .sort((a, b) => a.dateFrom.localeCompare(b.dateFrom))
            .map((bill) => ({
              label: bill.name,
              value: bill.id,
            }))}
          value={selectedBills}
          onChange={setSelectedBills}
          placeholder="Wähle Spesenabrechnungen aus"
        />
      </Box>
      <Button
        colorScheme="primary"
        onClick={handleTriggerBillClose}
        isLoading={isLoading}
        width="332px"
        isDisabled={selectedBills.length === 0}
      >
        {isLoading
          ? "Spesenabrechnungen werden geschlossen..."
          : "Spesenabrechnungen jetzt schliessen"}
      </Button>
    </VStack>
  );
};

export default BexioSettings;
